import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { ErrorSummary, type SummaryIssue } from '../../job-roles/components/ErrorSummary';
import type { CompanyWorkLocationOption } from '../../people/types';
import type { EarningCodeResponse } from '../../pay-config/types';
import { healthcareErrorMessage } from '../../healthcare-rates/healthcareErrors';
import { healthcareRatesGateway } from '../../healthcare-rates/index';
import { formatQuantity, parseQuantity, parseWorkedHours } from '../../healthcare-rates/money';
import { useSupportedPayBases } from '../../healthcare-rates/useSupportedPayBases';
import {
  WORKED_HOURS_HELP, payBasisLabels, quantityUnits, rateLabels,
  type DisciplineAssignment, type PayBasis, type ServiceLine, type ServiceType,
} from '../../healthcare-rates/types';
import type { EarningLineInput } from '../types';
import '../../job-roles/job-roles.css';

type DraftLine = EarningLineInput & { key: string };

export interface ServiceLineFormProps {
  employeeId: string;
  /** The run's pay period: a service date must lie inside it (the server defaults it to the period end date). */
  periodStart: string;
  periodEnd: string;
  codes: EarningCodeResponse[];
  locations: CompanyWorkLocationOption[];
  initial: DraftLine | null;
  submitLabel: string;
  onSubmit: (line: DraftLine) => void;
  onCancel: () => void;
}

/** A label, its control, an optional hint and an optional error, kept apart so the label's own text is the control's accessible name. */
function Field({ id, label, hint, error, children }: { id: string; label: string; hint?: ReactNode; error?: string | undefined; children: ReactNode }) {
  return (
    <div className="cpr-svc-field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && <span id={`${id}-hint`} className="muted">{hint}</span>}
      {error && <span id={`${id}-error`} className="error" role="alert">{error}</span>}
    </div>
  );
}

type Key = 'code' | 'discipline' | 'service' | 'quantity' | 'hours' | 'date' | 'reason';

/**
 * An earning line priced from the rate schedule: the preparer chooses WHO the pay is for (one of the employee's active disciplines), WHAT work it was (a service/visit type),
 * HOW it is paid (a pay basis) and HOW MANY (a quantity, plus the worked hours if known). The rate is NEVER typed here: EPN resolves it when the run is calculated and shows the
 * rate, where it came from and an explanation under the line. Quantity and hours are parsed from text; nothing here multiplies an amount.
 */
export function ServiceLineForm({ employeeId, periodStart, periodEnd, codes, locations, initial, submitLabel, onSubmit, onCancel }: ServiceLineFormProps) {
  const uid = useId();
  const first = initial?.serviceLine ?? null;
  const [codeId, setCodeId] = useState(initial?.earningCodeId ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [disciplineId, setDisciplineId] = useState(first?.disciplineId ?? '');
  const [pickedServiceTypeId, setServiceTypeId] = useState(first?.serviceTypeId ?? '');
  const [payBasis, setPayBasis] = useState<PayBasis>(first?.payBasis ?? 'PerVisit');
  const [quantity, setQuantity] = useState(first ? formatQuantity(first.quantity).replace(/,/g, '') : '');
  const [hours, setHours] = useState(first?.workedHours != null ? formatQuantity(first.workedHours).replace(/,/g, '') : '');
  const [serviceDate, setServiceDate] = useState(first?.serviceDate ?? '');
  const [locationId, setLocationId] = useState(first?.workLocationId ?? '');
  const [authorized, setAuthorized] = useState(first?.additionalEarningAuthorized ?? false);
  const [reason, setReason] = useState(first?.additionalEarningReason ?? '');
  const [errors, setErrors] = useState<Partial<Record<Key, string>>>({});
  const [focusNonce, setFocusNonce] = useState(0);
  const [assignments, setAssignments] = useState<DisciplineAssignment[] | null>(null);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** The ids of the work types this employee may be paid for; null = no list (no limit). */
  const [allowed, setAllowed] = useState<string[] | null>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const asOf = serviceDate || periodEnd;
  // A work type that is not on the employee's list is never carried into the line (a line loaded for editing may name one the list no longer allows).
  const serviceTypeId = allowed && !allowed.includes(pickedServiceTypeId) ? '' : pickedServiceTypeId;

  useEffect(() => {
    const controller = new AbortController();
    healthcareRatesGateway
      .listActiveDisciplineAssignments(employeeId, asOf, controller.signal)
      .then((rows) => { if (!controller.signal.aborted) { setAssignments(rows); setLoadError(null); } })
      .catch((err: unknown) => { if (!controller.signal.aborted) { setAssignments([]); setLoadError(healthcareErrorMessage(err, 'Could not load this employee’s disciplines.')); } });
    return () => controller.abort();
  }, [employeeId, asOf]);

  useEffect(() => {
    const controller = new AbortController();
    healthcareRatesGateway.listServiceTypes(undefined, controller.signal).then((rows) => { if (!controller.signal.aborted) setServiceTypes(rows); }).catch(() => undefined);
    return () => controller.abort();
  }, []);

  // The work types this employee may be paid for (universal compensation phase C-1). No list = no limit; if it cannot be read the full list is offered and the server still refuses a work type that is not allowed.
  useEffect(() => {
    const controller = new AbortController();
    healthcareRatesGateway
      .getAllowedWorkTypes(employeeId, controller.signal)
      .then((result) => { if (!controller.signal.aborted) setAllowed(result.isRestricted ? result.items.map((i) => i.serviceTypeId) : null); })
      .catch(() => { if (!controller.signal.aborted) setAllowed(null); });
    return () => controller.abort();
  }, [employeeId]);

  useEffect(() => {
    if (focusNonce > 0) summaryRef.current?.focus();
  }, [focusNonce]);

  // One entry per discipline the employee holds on the service date (an employee may hold several).
  const disciplines = useMemo(
    () => [...new Map((assignments ?? []).map((a) => [a.disciplineId, a])).values()].sort((a, b) => a.disciplineName.localeCompare(b.disciplineName)),
    [assignments],
  );
  const payBases = useSupportedPayBases();
  const chosenService = serviceTypes.find((t) => t.id === serviceTypeId);
  const unit = quantityUnits[payBasis].plural;
  const fid = (k: string) => `${uid}-${k}`;
  const order: Key[] = ['code', 'discipline', 'service', 'quantity', 'hours', 'date', 'reason'];
  const issues: SummaryIssue[] = order.filter((k) => errors[k]).map((k) => ({ key: k, message: errors[k]! }));
  const clearError = (k: Key) => setErrors((prev) => { const next = { ...prev }; delete next[k]; return next; });
  const focusField = (key: string) => rootRef.current?.querySelector<HTMLElement>(`[data-field="${key}"]`)?.focus();
  const describe = (k: Key, extra?: string) => [errors[k] ? `${fid(k)}-error` : null, extra].filter(Boolean).join(' ') || undefined;
  const dateHint = `Optional. It must be inside the pay period (${periodStart} to ${periodEnd}); it defaults to ${periodEnd}.`;

  function submit() {
    const found: Partial<Record<Key, string>> = {};
    if (!codeId) found.code = 'Choose an earning code (it decides how the pay is taxed).';
    if (!disciplineId) found.discipline = disciplines.length === 0 ? 'This employee holds no worker category on that date. Add one in their profile first.' : 'Choose one of the employee’s worker categories.';
    if (allowed && !serviceTypeId) found.service = 'Choose a work type: this employee may be paid only for specific work types.';
    const q = parseQuantity(quantity);
    if (quantity.trim() === '') found.quantity = `Enter how many ${unit}.`;
    else if (q === null) found.quantity = 'Enter a quantity above 0 and up to 10,000, with up to 4 decimals.';
    const h = hours.trim() === '' ? null : parseWorkedHours(hours);
    if (hours.trim() !== '' && h === null) found.hours = 'Enter worked hours from 0 to 744, with up to 2 decimals, or leave it empty.';
    if (serviceDate && (serviceDate < periodStart || serviceDate > periodEnd)) found.date = `The service date must be inside the pay period (${periodStart} to ${periodEnd}).`;
    if (authorized && reason.trim() === '') found.reason = 'Give a reason for the additional earning.';
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setFocusNonce((n) => n + 1);
      return;
    }
    const discipline = disciplines.find((d) => d.disciplineId === disciplineId);
    const serviceLine: ServiceLine = {
      disciplineId, serviceTypeId: serviceTypeId || null, payBasis, quantity: q!, workedHours: h, serviceDate: serviceDate || null, workLocationId: locationId || null,
      additionalEarningAuthorized: authorized, additionalEarningReason: authorized ? reason.trim() : null,
      disciplineCode: discipline?.disciplineCode ?? null, disciplineName: discipline?.disciplineName ?? null,
      serviceTypeCode: chosenService?.code ?? null, serviceTypeName: chosenService?.name ?? null,
    };
    const code = codes.find((c) => c.id === codeId);
    onSubmit({
      key: initial?.key ?? crypto.randomUUID(),
      earningCodeId: codeId,
      label: label.trim() || [discipline?.disciplineName, chosenService?.name].filter(Boolean).join(' / ') || code?.name || 'Service line',
      // A service line never carries any of these: the server refuses a rate, an amount or hours next to it.
      hours: null, rate: null, flatAmount: null, serviceLine,
    });
  }

  return (
    <div ref={rootRef} className="cpr-earning-form cpr-service-form" role="group" aria-label={initial ? 'Edit service line' : 'Add service line'}>
      <ErrorSummary ref={summaryRef} issues={issues} onSelect={focusField} />
      <div className="cpr-service-grid">
        <Field id={fid('code')} label="Earning code" error={errors.code}>
          <select
            id={fid('code')} data-field="code" value={codeId} aria-required="true" aria-invalid={errors.code ? true : undefined} aria-describedby={describe('code')}
            onChange={(e) => { setCodeId(e.target.value); clearError('code'); }}
          >
            <option value="">Choose…</option>
            {codes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field id={fid('label')} label="Label (optional)">
          <input id={fid('label')} value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>
        <Field id={fid('discipline')} label="Worker category" hint="Only the worker categories this employee holds." error={errors.discipline}>
          <select
            id={fid('discipline')} data-field="discipline" value={disciplineId} aria-required="true" aria-invalid={errors.discipline ? true : undefined}
            aria-describedby={describe('discipline', `${fid('discipline')}-hint`)} onChange={(e) => { setDisciplineId(e.target.value); clearError('discipline'); }}
          >
            <option value="">{assignments === null ? 'Loading…' : disciplines.length === 0 ? 'No worker category held on this date' : 'Choose a worker category'}</option>
            {disciplines.map((d) => <option key={d.disciplineId} value={d.disciplineId}>{d.disciplineName}</option>)}
          </select>
        </Field>
        <Field
          id={fid('service')} label="Work type" error={errors.service}
          hint={
            <>
              {allowed ? 'The work performed. This employee may be paid only for these work types (set in their Payroll Details).' : 'The work performed.'}
              {chosenService?.suggestedPayBasis && <> Suggested pay basis: {payBasisLabels[chosenService.suggestedPayBasis]} (a hint only).</>}
            </>
          }
        >
          <select
            id={fid('service')} data-field="service" value={serviceTypeId} aria-required={allowed ? true : undefined} aria-invalid={errors.service ? true : undefined}
            aria-describedby={describe('service', `${fid('service')}-hint`)} onChange={(e) => { setServiceTypeId(e.target.value); clearError('service'); }}
          >
            <option value="">{allowed ? 'Choose a work type' : 'No specific work type'}</option>
            {serviceTypes.filter((t) => t.isActive && (!allowed || allowed.includes(t.id))).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        <Field id={fid('basis')} label="Pay basis">
          <select id={fid('basis')} value={payBasis} onChange={(e) => setPayBasis(e.target.value as PayBasis)}>
            {payBases.map((b) => <option key={b} value={b}>{payBasisLabels[b]}</option>)}
          </select>
        </Field>
        <Field id={fid('quantity')} label="Quantity" hint={`The number of ${unit}.`} error={errors.quantity}>
          <input
            id={fid('quantity')} data-field="quantity" inputMode="decimal" value={quantity} autoComplete="off" aria-required="true" aria-invalid={errors.quantity ? true : undefined}
            aria-describedby={describe('quantity', `${fid('quantity')}-hint`)} onChange={(e) => { setQuantity(e.target.value); clearError('quantity'); }}
          />
        </Field>
        <Field id={fid('hours')} label="Worked hours (optional)" hint={WORKED_HOURS_HELP} error={errors.hours}>
          <input
            id={fid('hours')} data-field="hours" inputMode="decimal" value={hours} autoComplete="off" aria-invalid={errors.hours ? true : undefined}
            aria-describedby={describe('hours', `${fid('hours')}-hint`)} onChange={(e) => { setHours(e.target.value); clearError('hours'); }}
          />
        </Field>
        <Field id={fid('date')} label="Service date (optional)" hint={dateHint} error={errors.date}>
          <input
            id={fid('date')} data-field="date" type="date" min={periodStart} max={periodEnd} value={serviceDate} aria-invalid={errors.date ? true : undefined}
            aria-describedby={describe('date', `${fid('date')}-hint`)} onChange={(e) => { setServiceDate(e.target.value); clearError('date'); }}
          />
        </Field>
        <Field id={fid('location')} label="Work location (optional)">
          <select id={fid('location')} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <option value="">No specific location</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
      </div>

      <div className="cpr-rate-readonly" data-testid="service-rate-readonly">
        <strong>{rateLabels[payBasis]}</strong>
        <p className="muted">
          Set by EPN when the run is calculated, from your rate schedule. It is never typed on a service line. After you calculate, the rate, where it came from and an
          explanation appear under the line.
        </p>
      </div>

      <div className="cpr-service-extra">
        <label className="cpr-check">
          <input type="checkbox" checked={authorized} onChange={(e) => { setAuthorized(e.target.checked); clearError('reason'); }} />
          <span>This is an additional earning (it may pay the same work on a second pay basis)</span>
        </label>
        {authorized && (
          <Field id={fid('reason')} label="Reason for the additional earning" error={errors.reason}>
            <input
              id={fid('reason')} data-field="reason" value={reason} maxLength={300} autoComplete="off" aria-required="true" aria-invalid={errors.reason ? true : undefined}
              aria-describedby={describe('reason')} onChange={(e) => { setReason(e.target.value); clearError('reason'); }}
            />
          </Field>
        )}
        <p className="muted">Two lines for the same date, worker category and work type on different pay bases are refused unless both are marked as additional earnings with a reason. The choice is recorded.</p>
      </div>
      {loadError && <p className="error" role="alert">{loadError}</p>}

      <div className="cpr-service-actions">
        <button type="button" className="cpr-btn" onClick={submit}>{submitLabel}</button>
        <button type="button" className="cpr-btn cpr-btn--ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
